import React, { FC, useMemo } from "react";
import { useSelector } from "react-redux";
import { useFormContext, useWatch } from "react-hook-form";

import { TransferProgramDetails } from "../../../../../programSearch/components/transferProgramDetails/TransferProgramDetails";
import {
  FormController,
  MuiGrid,
  MuiSelect,
  OptionsDataType,
} from "../../../../../../components";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import { useGridContainer } from "../../../../../../hooks/useGridContainer";
import {
  TransferProgramFormKey,
  transferTypeKey,
} from "../../../../modelsAndSchemas/Models";
import { bulkTransferSiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";
import { bulkTransferSearchInputSelector } from "../../../../../../redux/slices/program/programSelector";
import { InformationBadge } from "../../../../../../common/informationBadge/informationBadge";
import { ReactComponent as WarningIcon } from "../../../../../../assets/images/warning-icon.svg";
import { TransferProgramsWrapper } from "./TransferPrograms.style";
import { DestinationProgram } from "../../../../../createProgram/modelsAndSchemas/Models";
import { useNavigationSiteCore } from "../../../../../../hooks/useNavigationSiteCore";
interface TransferProgramsProps {
  pageId: string;
  selectedTransferType: string;
  countryListData: OptionsDataType[];
  deliveryOfficeList: OptionsDataType[];
  compatibleProgramTemplateList: OptionsDataType[];
  isNoDestinationTemplate: boolean;
  destinationProgramData: DestinationProgram;
  nextLanguageData: OptionsDataType[];
}

export const TransferPrograms: FC<TransferProgramsProps> = ({
  pageId,
  selectedTransferType,
  countryListData,
  deliveryOfficeList,
  compatibleProgramTemplateList,
  isNoDestinationTemplate,
  destinationProgramData,
  nextLanguageData,
}) => {
  const { commonLabels, fetchLabel } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { gridSizeMedium: gridSize } = useGridContainer();
  const sourceProgramDetails = useSelector(bulkTransferSearchInputSelector);
  const { control, getValues } = useFormContext();

  const getTransferLabel = (key: string) => {
    return fetchLabel(
      bulkTransferSiteCoreKeys.programsAndCandidatesTransferDetailsKey,
      key,
    );
  };

  const isTransferProgramsToCountry = useMemo(() => {
    return selectedTransferType === transferTypeKey.changeCountry;
  }, [selectedTransferType]);

  const nextLanguageWatch = useWatch({
    name: TransferProgramFormKey.nextLanguage,
    control,
  });
  const getSelectedNextLanguageLabel = useMemo(() => {
    if (isTransferProgramsToCountry) {
      const selectedLanguage = getValues(TransferProgramFormKey.nextLanguage);
      const selectedLanguageObj = nextLanguageData.find(
        (lang) => lang.value === selectedLanguage,
      );
      return selectedLanguageObj ? selectedLanguageObj.label : "";
    } else {
      return "";
    }
  }, [isTransferProgramsToCountry, nextLanguageWatch]);

  return (
    <TransferProgramsWrapper data-testid="transfer-programs">
      {isTransferProgramsToCountry ? (
        <MuiGrid container>
          <MuiGrid size={gridSize} className="transfer-field">
            <FormController
              control={control}
              name={TransferProgramFormKey.destinationCountry}
            >
              <MuiSelect
                label={getTransferLabel(
                  bulkTransferSiteCoreKeys.destinationCountry,
                )}
                options={countryListData}
                visibleDefaultSelectOption
                value=""
                id={`${pageId}-${TransferProgramFormKey.destinationCountry}`}
                placeholder={commonLabels.selectPlaceHolder}
                isMandatory
                showSearch
                searchPlaceholder={commonLabels.search}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>
          <MuiGrid size={gridSize} className="transfer-field">
            <FormController
              control={control}
              name={TransferProgramFormKey.deliveryOffice}
            >
              <MuiSelect
                label={getTransferLabel(
                  bulkTransferSiteCoreKeys.deliveryOffice,
                )}
                options={deliveryOfficeList}
                visibleDefaultSelectOption
                value=""
                id={`${pageId}-${TransferProgramFormKey.deliveryOffice}`}
                placeholder={commonLabels.selectPlaceHolder}
                isMandatory
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>

          <MuiGrid size={gridSize} className="transfer-field">
            <FormController
              control={control}
              name={TransferProgramFormKey.nextLanguage}
            >
              <MuiSelect
                label={getTransferLabel(bulkTransferSiteCoreKeys.nextLanguage)}
                options={nextLanguageData}
                visibleDefaultSelectOption
                value=""
                id={`${pageId}-${TransferProgramFormKey.nextLanguage}`}
                placeholder={commonLabels.selectPlaceHolder}
                isMandatory
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>
        </MuiGrid>
      ) : (
        <MuiGrid container>
          <MuiGrid size={gridSize} className="transfer-field">
            <FormController
              control={control}
              name={TransferProgramFormKey.compatibleProgramTemplate}
            >
              <MuiSelect
                label={getTransferLabel(
                  bulkTransferSiteCoreKeys.compatibleProgramTemplate,
                )}
                options={compatibleProgramTemplateList}
                visibleDefaultSelectOption
                value=""
                id={`${pageId}-${TransferProgramFormKey.compatibleProgramTemplate}`}
                placeholder={commonLabels.selectPlaceHolder}
                isMandatory
                showSearch
                searchPlaceholder={commonLabels.search}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>
        </MuiGrid>
      )}
      <TransferProgramDetails
        pageId={pageId}
        sourceProgramData={{
          deliveryCountryName: sourceProgramDetails?.deliveryCountryName,
          solutionOffering: sourceProgramDetails?.nextViewName,
          programTemplate: sourceProgramDetails?.programTemplateName,
        }}
        isNoDestinationTemplate={isNoDestinationTemplate}
        showDeliveryOffice={isTransferProgramsToCountry}
        destinationProgramData={{
          ...destinationProgramData,
          deliveryOffice: {
            countryCode: destinationProgramData?.deliveryOfficeId ?? "",
            countryName: destinationProgramData?.deliveryOfficeName ?? "",
          },
          selectedNextLanguage: getSelectedNextLanguageLabel,
        }}
        isTransferToCountry={isTransferProgramsToCountry}
      />

      {isNoDestinationTemplate && (
        <InformationBadge
          message={getTransferLabel(
            bulkTransferSiteCoreKeys.noCompatibleProgramTemplateAlertMessage,
          )}
          icon={<WarningIcon className="warning-icon" />}
          position="start"
        />
      )}
    </TransferProgramsWrapper>
  );
};
